import { Box, IconButton, Stack, Typography } from "@mui/material";

/**
 * The top of every drawer in every Kernel app.
 *
 * ## The three lines, and why the third is not optional
 *
 * A drawer opens because somebody clicked something, and the thing they
 * clicked is now hidden behind the drawer. So the panel has to re-establish
 * what it is about before it says anything else — and for a person who does
 * not already know this product, "what it is about" is three separate
 * questions that were being answered by one monospace id:
 *
 * 1. **`title`** — the friendly name. Never monospace, never an id. A panel
 *    headed `receivables-review` tells somebody who already knows what it is
 *    that they were right, and tells everybody else nothing.
 * 2. **`id`** — the string the kernel keys this by, small and underneath.
 *    Kept, because somebody reading a ceiling or filing a bug needs it, and
 *    demoted, because almost nobody else does.
 * 3. **`lede`** — one sentence: what this panel is, and what a person can do
 *    on it. This is the line that was missing from all six drawers, and it
 *    is the one a new person needs most. It is a required prop rather than
 *    an optional one for exactly that reason — a drawer that cannot say in a
 *    sentence what it is for is a drawer whose contents nobody can place.
 *
 * `autoFocus` on the close button is not a flourish. Opening a drawer puts
 * `aria-hidden` on the app behind it, and the control that was clicked is in
 * there still holding focus — which the browser reports as focus hidden from
 * assistive technology, and which means a keyboard user's next Tab starts
 * from somewhere that no longer exists.
 */
export default function DrawerHead({ title, id, lede, chips, onClose }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}
          >
            <Typography variant="h1" sx={{ overflowWrap: "anywhere", minWidth: 0 }}>
              {title}
            </Typography>
            {chips}
          </Stack>
          {id ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontFamily: "ui-monospace, monospace",
                overflowWrap: "anywhere",
                mt: 0.25,
              }}
            >
              {id}
            </Typography>
          ) : null}
        </Box>
        <IconButton onClick={onClose} aria-label="Close" size="small" autoFocus>
          ✕
        </IconButton>
      </Stack>

      {/* The lede sits under the id rather than beside the title, so it gets
          the full width and reads as a sentence instead of a caption. */}
      <Typography variant="body2" sx={{ mt: 1.25, maxWidth: "62ch" }}>
        {lede}
      </Typography>
    </Box>
  );
}
